import BarberDefaultImage from '@assets/images/barber-default.png';
import { FlexRow } from '@components/FlexRow';
import CopyToClipboardIcon from '@components/Icon/Copy';
import EyeIcon from '@components/Icon/Eye';
import TrashRedCircleIcon from '@components/Icon/TrashRedCircle';
import formatDate from '@helper/formatDate';
import formatNumberToK from '@helper/formatNumbertoK';
import { JobApplicationStatus } from '@modules/jobApplications/statuses';
import { Avatar, Box, Card, styled, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';

const CardWrapper = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2),
    borderRadius: '12px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    backgroundColor: "#0C0D18",
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
    width: '100%',
    height: '100%',
    flexDirection: 'column',
    gap: theme.spacing(2),
    backgroundImage: 'none',
}));

const TopSection = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    overflow: 'hidden',
}));

const HeaderRow = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
}));

const CreatedBy = styled(Box)(({ theme }) => ({
    fontSize: '12px',
    fontWeight: 500,
    color: theme.palette.textSecondary.main,
}));


const AssigneeSection = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
}));

const AssigneeLabel = styled(Box)(({ theme }) => ({
    fontSize: '12px',
    fontWeight: 400,
    color: theme.palette.textSecondary.main,
}));

const AvatarGroup = styled(Box)(() => ({
    display: 'inline-flex',
    position: 'relative',

    '& .MuiAvatar-root': {
        height: '16px',
        width: '16px',
        position: 'absolute',

        '&:nth-of-type(1)': {
            left: '0px',
        },
        '&:nth-of-type(2)': {
            left: '12px',
        },
        '&:nth-of-type(3)': {
            left: '24px',
        },
    },
}));

const ContentSection = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
}));

const CompanyRow = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
}));

const CompanyLogo = styled('img')(() => ({
    width: '13px',
    height: '13px',
    transform: 'rotate(180deg) scaleY(-1)',
}));

const CompanyName = styled(Box)(({ theme }) => ({
    fontSize: '13px',
    fontWeight: 500,
    color: theme.palette.textSecondary.main,
    flex: 1,
}));

const JobInfo = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
}));


const JobNumberRow = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginBottom: '4px',
}));

const JobNumber = styled(Box)(({ theme }) => ({
    fontSize: '13px',
    fontWeight: 500,
    color: theme.palette.textSecondary.main,
}));

const CopyIcon = styled(CopyToClipboardIcon)(() => ({
    width: '12px',
    height: '12px',
    cursor: 'pointer',
}));

const StatsSection = styled(Box)(() => ({
    display: "grid",
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
}));

const StatCard = styled(Box)(({ theme }) => ({
    backgroundColor: '#000000',
    borderRadius: '8px',
    padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    width: '100%',
    minHeight: '51px',
}));

const StatValue = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#ffffff',
}));

const StatLabel = styled(Box)(() => ({
    fontSize: '12px',
    fontWeight: 400,
    color: '#f9f9f9',
}));

const StatYear = styled(Box)(() => ({
    fontSize: '10px',
    fontWeight: 400,
    color: '#ffffff',
}));

const DeleteIcon = styled(Box)(() => ({
    width: '20px',
}));

interface JobOfferCardProps {
    job: any;
    disableLink?: boolean;
    onConfirm?: (params: {
        title: string;
        description: string;
        confirmTitle: string;
        cancelTitle: string;
        action: string;
    }) => void;
    setSelectedId?: (id: string) => void;
}

const JobOfferCard = ({ job, disableLink, onConfirm, setSelectedId }: JobOfferCardProps) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async (value: string) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    };
    
    // Calculate job duration in days
    const getDurationInDays = () => {
        if (!job.publishDate || !job.expiredDate) return 'N/A';
        const start = new Date(job.publishDate);
        const end = new Date(job.expiredDate);
        const diffTime = Math.abs(end.getTime() - start.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return `${diffDays} Jours`;
    };

    // Get application count
    const getApplicationCount = () => {
        if (!job.jobApplicationsStats || !job.jobApplicationsStats.length) return 0;
        return job.jobApplicationsStats
            .filter((stat: any) => stat._id !== JobApplicationStatus.Hired && stat._id !== JobApplicationStatus.Rejected)
            .reduce((total: number, stat: any) => total + stat.count, 0);
    };

    // Format created by info
    const getCreatedByInfo = useCallback(() => {
        const createdDate = formatDate(job.createdDate, 'DD.MM.YY (HH:mm)');

        if (job.createdBy) {
            return (
                <>
                    par {job.createdBy?.firstName}<br />
                    {createdDate}
                </>
            );
        }

        if (job.pro?.bossFirstname) {
            return (
                <>
                    par {job.pro?.bossFirstname}<br />
                    {createdDate}
                </>
            );
        }

        return (
            <>
                par {job.pro?.commercialName}<br />
                {createdDate}
            </>
        );
    }, [job]);

    // Get brand logo
    const getBrandLogo = () => {
        if (job.brand?.brandLogo?.url) {
            return job.brand.brandLogo.url;
        }
        if (job.pro?.logo) {
            return job.pro.logo;
        }
        return BarberDefaultImage;
    };

    return (
        <CardWrapper>
            <TopSection>
                <HeaderRow>
                    <FlexRow gap={45} flex={1} sx={{ alignItems: 'start' }}>
                        <CreatedBy flex={1}>
                            {getCreatedByInfo()}
                        </CreatedBy>
                        <AssigneeSection flex={1}>
                            <AssigneeLabel>
                                Assigné à :
                            </AssigneeLabel>
                            <AvatarGroup>
                                {job.assignees ? job.assignees.slice(0, 3).map((assignee: any, index: number) => {
                                    // Handle new polymorphic assignee structure
                                    if (assignee.assigneeDetails) {
                                        const details = assignee.assigneeDetails;
                                        const avatarProps = assignee.assigneeType === 'OrganizationUser'
                                            ? {
                                                alt: `${details.firstName} ${details.lastName}`,
                                                src: details.file?.url
                                            }
                                            : {
                                                alt: details.commercialName || `${details.bossFirstname} ${details.bossLastname}`,
                                                src: details.bossPhoto
                                            };

                                        return (
                                            <Avatar
                                                key={assignee._id || index}
                                                {...avatarProps}
                                            />
                                        );
                                    }
                                    // Legacy fallback
                                    return (
                                        <Avatar
                                            key={assignee._id || index}
                                            alt={`${assignee.firstName} ${assignee.lastName}`}
                                            src={assignee.file?.url}
                                        />
                                    );
                                }) : 'N/A'}
                            </AvatarGroup>
                        </AssigneeSection>
                    </FlexRow>
                    {job.active && onConfirm && (
                        <DeleteIcon>
                            <IconButton
                                sx={{ padding: 0 }}
                                onClick={e => {
                                    onConfirm?.({
                                        title: 'Are you sure you want to deactivate this Job Offer?',
                                        description:
                                            'By deactivate this job offer, User wont  be able to see this Job offer again. ',
                                        confirmTitle: 'Deactivate',
                                        cancelTitle: 'Cancel',
                                        action: 'deactivate',
                                    });
                                    setSelectedId?.(job._id);
                                }}>
                                <TrashRedCircleIcon />
                            </IconButton>
                        </DeleteIcon>
                    )}
                </HeaderRow>

                <ContentSection>
                    <CompanyRow>
                        <CompanyLogo
                            src={getBrandLogo()}
                            alt="company-logo"
                        />
                        <CompanyName>
                            {job.brand?.brandName || job.pro?.commercialName}
                        </CompanyName>
                    </CompanyRow>

                    <JobInfo>
                        <Box
                            component={Link}
                            to={`/job-offers/edit/${job.jobNumber}`}
                            sx={{
                                fontSize: '16px',
                                fontWeight: 500,
                                fontFamily: '"LEMON MILK Pro", sans-serif',
                                color: theme => theme.palette.textLink.main,
                                lineHeight: 'normal',
                            }}
                        >
                            {job.title}
                        </Box>

                        <JobNumberRow>
                            <JobNumber>
                                {job.jobNumber}
                            </JobNumber>
                            <Tooltip open={copied} title="Copy to clipboard" placement="top">
                                <CopyIcon
                                    onClick={() => handleCopy(job.jobNumber)}
                                />
                            </Tooltip>
                        </JobNumberRow>
                    </JobInfo>
                    <FlexRow gap={12} justifyContent='space-between'>
                        <FlexRow flex={1} sx={{ gap: 0.5 }}>
                            <Typography color="textSecondary" fontSize={14} component='i' className="fi fi-br-map-marker" />
                            <Typography color="textSecondary" fontSize={14}>
                                {job.city || job.address || 'N/A'}
                            </Typography>
                        </FlexRow>
                        <FlexRow flex={1} sx={{ gap: 0.5 }}>
                            <Typography color="textSecondary" fontSize={14} component='i' className="fi fi-br-briefcase" />
                            <Typography color="textSecondary" fontSize={14}>
                                {job.contract?.contractType || job.position?.experience || 'N/A'}
                            </Typography>
                        </FlexRow>
                    </FlexRow>
                </ContentSection>
            </TopSection>

            <StatsSection sx={{ marginTop: 'auto' }}>
                <StatCard>
                    <StatValue>
                        {job.views || 0}
                        <EyeIcon style={{ width: '12px', height: '12px' }} />
                    </StatValue>
                    <StatLabel>Vues uniques</StatLabel>
                </StatCard>

                <StatCard>
                    <StatValue>
                        {getApplicationCount()}
                        {/* <Box onClick={() => goToTalentList(job)} sx={{ cursor: disableLink ? 'default' : 'pointer', display: 'inline-flex' }}>
                            <Box sx={{
                                display: 'inline-flex',
                                position: 'relative',
                                width: '40px',
                                height: '16px',
                                '& .MuiAvatar-root': {
                                    position: 'absolute',
                                    width: '16px',
                                    height: '16px',
                                    '&:nth-of-type(1)': { left: '0px' },
                                    '&:nth-of-type(2)': { left: '12px' },
                                    '&:nth-of-type(3)': { left: '24px' },
                                }
                            }}>
                                <Avatar alt="Talent 1" />
                                <Avatar alt="Talent 2" />
                                <Avatar alt="Talent 3" />
                            </Box>
                        </Box> */}
                    </StatValue>
                    <StatLabel>En cours</StatLabel>
                </StatCard>

                <StatCard>
                    <StatValue>
                        {job?.contract?.remuneration?.min && job?.contract?.remuneration?.max
                            ? `${formatNumberToK(job.contract.remuneration.min)} - ${formatNumberToK(job.contract.remuneration.max)} €`
                            : 'N/A'}
                        {job?.contract?.remuneration?.min && job?.contract?.remuneration?.max && (
                            <StatYear component="span">
                                / {job?.contract?.isAnnualSalary ? 'an' : 'mois'}
                            </StatYear>
                        )}
                    </StatValue>
                    <StatLabel>Salaire</StatLabel>
                </StatCard>

                <Tooltip
                    title={`Publish on ${formatDate(job?.publishDate, 'DD.MM.YY (hh:mm)')}`}
                    placement="top"
                    arrow
                    slotProps={{
                        popper: {
                            modifiers: [
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [0, -14],
                                    },
                                },
                            ],
                        },
                        tooltip: {
                            sx: {
                                background: theme => theme.palette.backgroundPrimary.main,
                                color: '#fff',
                                fontWeight: 700,
                                fontSize: '13px',
                                lineHeight: '16px',
                            },
                        },
                        arrow: {
                            sx: {
                                color: theme => theme.palette.backgroundPrimary.main,
                            },
                        },
                    }}>
                    <StatCard>
                        <StatValue>
                            {getDurationInDays()}
                        </StatValue>
                        <StatLabel>Durée</StatLabel>
                    </StatCard>
                </Tooltip>
            </StatsSection>
        </CardWrapper>
    );
};

export default JobOfferCard;
